import React, { type ReactNode, useEffect } from 'react'
import { Button, Dialog } from '@atoms'
import { AppAlerts } from '@components/molecules/Alerts/AppAlerts'
import AppErrorsAlert from '@components/molecules/Alerts/AppErrorsAlert'
import { CsrfTokenField } from '@atoms/CsrfTokenField'
import type { DialogSize } from '@atoms/Dialog'
import { scopedTranslation } from '@frontend/utils/I18n'

const t = scopedTranslation('shared.buttons')

type FormDialogProps = {
  children: ReactNode
  open: boolean
  onOpenChange: (open: boolean) => void
  size?: DialogSize
  testId?: string
  preventOutsideClick?: boolean
  hasTinyMCE?: boolean
}

function FormDialogRoot({
  children,
  open,
  onOpenChange,
  size = 'xl',
  testId,
  preventOutsideClick = false,
  hasTinyMCE = false,
}: FormDialogProps) {
  if (hasTinyMCE) {
    return (
      <TinyMCEDialogRoot
        children={children}
        open={open}
        onOpenChange={onOpenChange}
        size={size}
        testId={testId}
        preventOutsideClick={preventOutsideClick}
      />
    )
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange} modal={true}>
      <Dialog.Content
        data-testid={testId}
        scroll={true}
        size={size}
        onPointerDownOutside={
          preventOutsideClick
            ? (e) => {
                e.preventDefault()
              }
            : undefined
        }
      >
        {children}
      </Dialog.Content>
    </Dialog.Root>
  )
}

function TinyMCEDialogRoot({
  children,
  open,
  onOpenChange,
  size,
  testId,
  preventOutsideClick,
}: Omit<FormDialogProps, 'hasTinyMCE'>) {
  // Prevents scrolling on the body when the dialog is open
  useEffect(() => {
    if (open) {
      document.body.classList.add('tw:overflow-hidden')
    } else {
      document.body.classList.remove('tw:overflow-hidden')
    }

    return () => {
      document.body.classList.remove('tw:overflow-hidden')
    }
  }, [open])

  const isTinyMCEElement = (target: Element) => {
    return target.closest('.tox-tinymce-aux, .tox-dialog, .tox-silver-sink')
  }

  const handleInteractOutside = (e: Event) => {
    const target = e.target as Element
    if (target && isTinyMCEElement(target)) {
      e.preventDefault()
      return
    }

    if (preventOutsideClick) {
      e.preventDefault()
    }
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    const isBackdropClick = e.target === e.currentTarget
    const shouldAllowClose = !preventOutsideClick

    if (isBackdropClick && shouldAllowClose) {
      onOpenChange(false)
    }
  }

  return (
    <>
      {open && (
        <div className="tw:fixed tw:inset-0 tw:z-50 tw:bg-black/60" onClick={handleBackdropClick} />
      )}
      {/* Note: Avoids using the modal prop here as it conflicts with TinyMCE dialogs */}
      <Dialog.Root open={open} onOpenChange={onOpenChange} modal={false}>
        <Dialog.Content
          data-testid={testId}
          scroll={true}
          size={size}
          onInteractOutside={handleInteractOutside}
          onEscapeKeyDown={(e) => e.preventDefault()}
          className="tw:z-50"
        >
          {children}
        </Dialog.Content>
      </Dialog.Root>
    </>
  )
}

type FormDialogTitleProps = {
  children: ReactNode
  alertsPath?: string
  errorsPath?: string
}

function FormDialogTitle({ children, alertsPath, errorsPath }: FormDialogTitleProps) {
  return (
    <Dialog.Header>
      <Dialog.Title>{children}</Dialog.Title>
      {alertsPath && (
        <div className="tw:font-medium tw:pt-4 tw:empty:hidden">
          <AppAlerts alertsPath={alertsPath} />
        </div>
      )}
      {errorsPath && (
        <div className="tw:pt-4 tw:empty:hidden">
          <AppErrorsAlert errorsPath={errorsPath} />
        </div>
      )}
    </Dialog.Header>
  )
}

type FormDialogFormProps = {
  children: ReactNode
  onSubmit?: (e: React.FormEvent) => void
  action?: string
  method?: 'post' | 'get'
}

function FormDialogForm({ children, onSubmit, action, method }: FormDialogFormProps) {
  return (
    <form action={action} method={method} onSubmit={onSubmit}>
      {action && <CsrfTokenField />}
      {children}
    </form>
  )
}

type FormDialogBodyProps = {
  children: ReactNode
}

function FormDialogBody({ children }: FormDialogBodyProps) {
  return (
    <div className="tw:pb-5 tw:space-y-4" style={{ overflow: 'visible', height: 'auto' }}>
      {children}
    </div>
  )
}

type FormDialogFooterProps = {
  submitText?: string
  showCancel?: boolean
  showSubmit?: boolean
  isLoading?: boolean
  disabled?: boolean
  submitColor?: 'primary' | 'danger'
}

function FormDialogFooter({
  submitText = t('submit'),
  showCancel = true,
  showSubmit = true,
  isLoading = false,
  disabled = false,
  submitColor = 'primary',
}: FormDialogFooterProps) {
  return (
    // Sticky so the submit button stays in view while a long form scrolls; the -6s reach over the dialog's p-6 so nothing shows below it
    <Dialog.Footer className="tw:sticky tw:-bottom-6 tw:-mb-6 tw:gap-2 tw:border-t tw:border-gray-200 tw:bg-white tw:pt-4 tw:pb-6 tw:sm:gap-0">
      {showCancel && (
        <Dialog.Close asChild>
          <Button type="button" size="xl" rank="secondary">
            {t('cancel')}
          </Button>
        </Dialog.Close>
      )}
      {showSubmit && (
        <Button type="submit" size="xl" rank="primary" color={submitColor} disabled={isLoading || disabled}>
          {submitText}
        </Button>
      )}
    </Dialog.Footer>
  )
}

export const FormDialog = {
  Root: FormDialogRoot,
  Title: FormDialogTitle,
  Form: FormDialogForm,
  Body: FormDialogBody,
  Footer: FormDialogFooter,
}
